import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { TableHeader } from 'components/table/table-content';
import {
  MooringArticleCategory,
  MooringArticleType,
  MooringProductType,
  MooringArticleListItem,
  getArticleDimensionStringShort,
  MooringSupplier,
} from 'models/mooring';
import {
  ErpSystemService,
  MooringArticleCategoryService,
  MooringArticleService,
  MooringArticleTypeService,
  MooringProductTypeService,
} from 'services';
import { ErrorService } from 'services/error-service';
import { ToastService } from 'services/toast-service';
import { Router } from 'aurelia-router';
import { DataFilter } from 'lib/tables/DataFilter';
import { PubSub } from 'lib/event/PubSub';
import { Filters, filterTypes } from 'elements/Filter';
import { DialogService } from 'aurelia-dialog';
import { MooringArticleDialog } from './mooring-article-dialog';
import { SupplierService } from 'services/supplier-service';
import { FilterManager } from 'lib/tables/FilterManager';
import { ErpSystem } from 'models';

type TableFilter = {
  categoryIds?: string[];
  typeIds?: string[];
  productIds?: string[];
  supplierIds?: string[];
} & DataFilter;
type MooringArticleListItemWithDimension = MooringArticleListItem & { Dimension: string };
@autoinject()
export class MooringArticleContainer {
  protected articles: MooringArticleListItemWithDimension[] = [];
  protected dataFilter: TableFilter = new DataFilter();

  protected categories: MooringArticleCategory[] = [];
  protected types: MooringArticleType[] = [];
  protected productTypes: MooringProductType[] = [];
  protected suppliers: MooringSupplier[] = [];
  protected erpSystems: ErpSystem[] = [];

  protected canCheck: boolean = false;

  protected drawerName = 'mooring-article';

  protected tableHeaders: TableHeader<MooringArticleListItem & { actions: ''; Dimension: string; IsChecked: boolean; IsLocked: boolean }>[] = [
    { key: 'IsChecked', label: '', sortable: false },
    { key: 'IsLocked', label: '', sortable: false },
    { key: 'Id', label: 'general.id', sortable: true },
    { key: 'Category', label: 'general.category', sortable: true },
    { key: 'Type', label: 'general.type', sortable: true },
    { key: 'ProductType', label: 'general.product', sortable: true },
    { key: 'Dimension', label: 'general.dimension', sortable: false },
    { key: 'Supplier', label: 'general.supplier', sortable: true },
    { key: 'ErpSystem', label: 'general.erpSystem', sortable: false },
    { key: 'MBL', label: 'general.mbl', sortable: true },
    { key: 'actions', label: 'general.actions', sortable: false },
  ];

  constructor(
    private errorService: ErrorService,
    private mooringArticleCategoryService: MooringArticleCategoryService,
    private mooringArticleTypeService: MooringArticleTypeService,
    private mooringProductTypeService: MooringProductTypeService,
    private supplierService: SupplierService,
    private articleService: MooringArticleService,
    private erpSystemService: ErpSystemService,
    private toast: ToastService,
    private router: Router,
    private pubsub: PubSub,
    private t: I18N,
    private dialogService: DialogService,
    protected fm: FilterManager
  ) {
    pubsub.sub('filter-manager:filter-changed', () => {
      void this.getArticles();
    });

    this.pubsub.sub('list-entity:updated', () => {
      void this.getArticles();
    });

    this.pubsub.sub('list-entity:created', () => {
      void this.getArticles();
    });
  }

  protected openArticleForm(id?: number) {
    void this.dialogService
      .open({
        viewModel: MooringArticleDialog,
        model: id ? { articleId: id } : undefined,
        position: () => ({}),
      })
      .whenClosed(() => {
        document.querySelector('html').style.overflowY = null;
      });
  }

  protected async onDelete(id: number) {
    try {
      if (id) {
        await this.mooringArticleCategoryService.delete(id);
        this.toast.showSuccess(this.t.tr('general.deleted'));
        await this.getArticles();
      }
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  protected async attached() {
    await this.load();

    this.fm.init({
      context: 'mooring-article-register',
      storageKey: 'mooring-article-register',
      filterFilters: (filters) => {
        const categoryIds = Object.entries(filters.categoryIds.values)
          .filter(([, value]) => value)
          .map(([key]) => +key);

        filters.typeIds.options = this.types.filter((x) => categoryIds.includes(x.CategoryId)).map((x) => ({ Name: x.Name, Id: x.Id }));

        if (categoryIds.length === 0) {
          this.fm.resetFilter('typeIds');
          this.fm.resetFilter('productIds');
        }

        const productIds = Object.entries(filters.typeIds.values)
          .filter(([, value]) => value)
          .map(([key]) => +key);

        filters.productIds.options = this.productTypes
          .filter((x) => productIds.includes(x.ArticleTypeId))
          .map((x) => ({ Name: x.Name, Id: x.Id }));

        if (productIds.length === 0) {
          this.fm.resetFilter('productIds');
        }

        return filters;
      },
      filters: {
        categoryIds: {
          name: 'categoryIds',
          label: this.t.tr('general.category'),
          type: filterTypes.CHECKBOX,
          originalData: this.categories,
          options: this.categories.map((x) => ({ Name: x.Name, Id: x.Id })),
          query: 'categoryIds',
          values: {},
        },
        typeIds: {
          name: 'typeIds',
          label: this.t.tr('general.type'),
          type: filterTypes.CHECKBOX,
          originalData: this.types,
          options: this.types.map((x) => ({ Name: x.Name, Id: x.Id })),
          query: 'typeIds',
          values: {},
        },
        productIds: {
          name: 'productIds',
          label: this.t.tr('general.product'),
          type: filterTypes.CHECKBOX,
          originalData: this.productTypes,
          options: this.productTypes.map((x) => ({ Name: x.Name, Id: x.Id })),
          query: 'productIds',
          values: {},
        },
        supplierIds: {
          name: 'supplierIds',
          label: this.t.tr('general.supplier'),
          type: filterTypes.CHECKBOX,
          originalData: this.suppliers,
          options: this.suppliers.map((x) => ({ Name: x.Name, Id: x.Id })),
          query: 'supplierIds',
          values: {},
        },
        erpIds: {
          name: 'erpIds',
          label: this.t.tr('general.erpSystem'),
          type: filterTypes.CHECKBOX,
          originalData: this.erpSystems,
          options: this.erpSystems.map((x) => ({
            Name: x.Title,
            Id: x.Id,
          })),
          query: 'erpIds',
        },
      } satisfies Filters,
      defaultFilters: {
        categoryIds: true,
        typeIds: true,
        productIds: true,
        supplierIds: true,
        erpIds: true,
      },
    });
  }

  protected detached() {
    this.pubsub.unsub();
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  private async load() {
    const [categories, types, productTypes, suppliers, erpSystems] = await Promise.all([
      this.mooringArticleCategoryService.getAll(),
      this.mooringArticleTypeService.getAll(),
      this.mooringProductTypeService.getAll(),
      this.supplierService.getAll(),
      this.erpSystemService.getAll(),
    ]);

    this.categories = categories;
    this.types = types;
    this.productTypes = productTypes;
    this.suppliers = suppliers;
    this.erpSystems = erpSystems;
  }

  protected setOrderByColumn(column: string) {
    this.fm.setOrderByColumn(column);
  }

  protected async getArticles() {
    try {
      const response = await this.articleService.table(this.fm.queryFilter);
      if (!response) return;
      this.fm.paginationTotalRecords = +response.headers.get('x-total-count') || 0;
      this.articles = response.data.map((x) => {
        (x as MooringArticleListItemWithDimension)['Dimension'] = getArticleDimensionStringShort(x);
        return x as MooringArticleListItemWithDimension;
      });
      this.articles = this.articles.map((item) => ({
        ...item,
        isChecked: false,
      }));
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  protected setCanCheck() {
    if (!this.canCheck) {
      (this.articles as any[]).forEach((article) => {
        article.isChecked = false;
      });
    }
    this.canCheck = !this.canCheck;
  }

  protected async exportToDataSheet() {
    const selectedArticles = (this.articles as any[]).filter((article) => article.isChecked).map((article) => article.Id);

    if (selectedArticles.length < 1) {
      this.toast.showWarning('mooring.selectAtLeastOneArticle');
      return;
    }

    const articleIds = selectedArticles;
    await this.articleService.exportToDataSheet(articleIds);

    this.canCheck = false;
    (this.articles as any[]).forEach((article) => {
      article.isChecked = false;
    });
  }
}
